import { GUEST_STATUS } from '@ibe/constants';
import { z } from 'zod';

/**
 * The Guests module's contracts.
 *
 * The directory endpoint returns no PII; the PII endpoint returns it only
 * after an audited reason. The form collects the same fields the write
 * endpoint will encrypt before saving.
 */

const MAX_EMAIL = 254;
const MAX_PHONE = 32;
const MAX_NAME = 100;
const MAX_ADDRESS_LINE = 255;
const MAX_POSTAL_CODE = 32;
const MAX_COUNTRY_CODE = 2;

export const guestRow = z.object({
  id: z.string(),
  displayName: z.string().optional(),
  status: z.string().optional(),
  stays: z.number().optional(),
  createdAt: z.string().optional(),
});

export type GuestRow = z.infer<typeof guestRow>;

export const guestAddress = z.object({
  id: z.string(),
  type: z.string(),
  line1: z.string(),
  line2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),
  countryCode: z.string().nullable(),
  isPrimary: z.boolean(),
});

export type GuestAddress = z.infer<typeof guestAddress>;

export const guestAccount = z.discriminatedUnion('hasAccount', [
  z.object({ hasAccount: z.literal(false) }),
  z.object({
    hasAccount: z.literal(true),
    status: z.string(),
    lastLoginAt: z.string().nullable(),
    passwordChangedAt: z.string().nullable(),
    emailVerifiedAt: z.string().nullable(),
    failedLoginAttempts: z.number(),
  }),
]);

export type GuestAccount = z.infer<typeof guestAccount>;

/**
 * The masked directory record used for the detail page shell.
 *
 * `version` is required because the edit form round-trips it on save. The
 * other fields are the only ones the directory endpoint returns; PII comes
 * from a separate, audited endpoint.
 */
export const guestDetail = z.object({
  id: z.string(),
  displayName: z.string(),
  status: z.string(),
  stays: z.number(),
  createdAt: z.string(),
  version: z.number(),
  addresses: z.array(guestAddress).optional(),
  account: guestAccount.optional(),
});

export type GuestDetail = z.infer<typeof guestDetail>;

/** The plaintext PII returned by the audited read endpoint. */
export const guestPii = z.object({
  guestId: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  accessLogId: z.string(),
});

export type GuestPii = z.infer<typeof guestPii>;

/** The statuses a guest may be moved between, in the order they read. */
export const GUEST_STATUS_OPTIONS: readonly { value: string; label: string }[] = [
  { value: GUEST_STATUS.ACTIVE, label: 'Active' },
  { value: GUEST_STATUS.INACTIVE, label: 'Inactive' },
  { value: GUEST_STATUS.BLOCKED, label: 'Blocked' },
];

const statusValues = GUEST_STATUS_OPTIONS.map((option) => option.value) as [string, ...string[]];

function optionalEmail() {
  return z
    .string()
    .max(MAX_EMAIL, 'That email address is too long')
    .refine((value) => value === '' || z.email().safeParse(value).success, {
      message: 'Enter a valid email address, or leave it blank',
    });
}

function optionalPhone() {
  return z.string().max(MAX_PHONE, 'That phone number is too long');
}

function optionalName(label: string) {
  return z.string().max(MAX_NAME, `${label} is too long`);
}

/**
 * What the edit form collects.
 *
 * Every value is a string: a controlled input cannot hold `null`, and an
 * empty PII field means "clear the stored value" on update. `status` is
 * required because a guest always has a status.
 */
function optionalAddressLine() {
  return z.string().max(MAX_ADDRESS_LINE);
}

export const GUEST_ACCOUNT_STATUS_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'PENDING_VERIFICATION', label: 'Pending verification' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'LOCKED', label: 'Locked' },
];

export const guestFormAddress = z.object({
  type: z.enum(['HOME', 'WORK', 'BILLING', 'OTHER'] as [string, ...string[]]),
  line1: z.string().max(MAX_ADDRESS_LINE),
  line2: optionalAddressLine(),
  city: optionalAddressLine(),
  state: optionalAddressLine(),
  postalCode: z.string().max(MAX_POSTAL_CODE),
  countryCode: z.string().max(MAX_COUNTRY_CODE),
});

export type GuestFormAddress = z.infer<typeof guestFormAddress>;

export const guestFormInput = z.object({
  firstName: optionalName('First name'),
  lastName: optionalName('Last name'),
  email: optionalEmail(),
  phone: optionalPhone(),
  status: z.enum(statusValues),
  accountStatus: z.string().optional(),
  address: guestFormAddress.optional(),
});

export type GuestFormInput = z.infer<typeof guestFormInput>;
